import { ComponentType } from 'react';
import { useDocumentParams } from 'app/router/params';
import { i18nMaybe } from 'shared/i18n';
import { Spinner } from 'app/components';
import Dropdown, { DropdownItem } from 'shared/forms/Dropdown';
import { useUser } from 'app/state/contexts';
import { useGetDocument, DesignEntry, DesignStyle } from 'entities/document';
import { useStyles } from 'entities/style';
import { VariableDefinition } from '../variable-schema';
import { defaultFontOptions, FontItem, extractFontName } from '../utils/font-options';
import './VariableField.scss';

interface FontFieldProps {
  def: VariableDefinition;
  value: string | number;
  onChange: (key: string, value: string) => void;
  onFieldHover?: (key: string | null) => void;
  variables?: Record<string, unknown>;
}

const isSetFontFamily = (v: unknown): v is string => !!v && v !== 'null';

function inheritedFont(key: string, variables: Record<string, unknown> = {}): string {
  const single = variables.xrpFontFamily1;
  if (isSetFontFamily(single)) return single;
  if (key.endsWith('font-bold')) return isSetFontFamily(variables.xrpHeadingBold) ? variables.xrpHeadingBold : '';
  if (key.endsWith('font-italic')) return isSetFontFamily(variables.xrpHeadingItalic) ? variables.xrpHeadingItalic : '';
  return isSetFontFamily(variables.xrpHeadingRegular) ? variables.xrpHeadingRegular : '';
}

export function FontField({ def, value, onChange, onFieldHover, variables }: FontFieldProps) {
  const { documentId } = useDocumentParams();
  const { selectedOrganization } = useUser();
  const pDocument = useGetDocument(documentId, selectedOrganization);
  const fontIds = pDocument.data?.content.design.filter((s: DesignEntry) => s.type === 'font').map((font: DesignEntry) => font.styleId) ?? [];
  const styleQuery = useStyles({ styleIds: fontIds, organization: selectedOrganization, documentRevisionId: pDocument.data?.revisionId });

  const hoverProps = onFieldHover ? { onMouseEnter: () => onFieldHover(def.key), onMouseLeave: () => onFieldHover(null) } : {};

  if (styleQuery.isLoading) {
    return <Spinner />;
  }

  const customFonts =
    (styleQuery.data as (DesignStyle | undefined)[] | undefined)
      ?.map((style) => extractFontName(style?.content as string))
      .filter(Boolean)
      .map((name: string) => ({ value: name, title: name })) ?? [];
  const items = [...defaultFontOptions, ...customFonts];
  const placeholder = inheritedFont(def.key, variables);

  return (
    <div className="variable-field d-flex flex-column gap-1" style={{ flex: '1 1 130px', minWidth: 0 }} {...hoverProps}>
      <label className="variable-field__label fs-7">{i18nMaybe(def.label)}</label>
      <Dropdown
        items={items}
        value={String(value)}
        onChange={(v) => onChange(def.key, v as string)}
        wrapper={FontItem as ComponentType<{ item: DropdownItem }>}
        sm
        placeHolder={placeholder}
        disabled={false}
        groupbyKey={undefined}
        children={undefined}
        filter={false}
      />
    </div>
  );
}
